import type { MouseEvent } from 'react';
import MenuDotsIcon from '../../assets/icons/menu-dots.svg?react';
import styles from './MenuDotsButton.module.css';

interface MenuDotsButtonProps {
  // Required: the button has no text, so this is its only accessible name. Name the
  // thing the menu acts on, not just "menu", so a list of them does not announce as
  // a row of identical buttons.
  ariaLabel: string;
  onClick: (e: MouseEvent<HTMLButtonElement>) => void;
  isExpanded?: boolean;
  // Set only when the popup really is a menu (role="menu" with menuitem children and
  // arrow-key navigation, as ContextMenu provides). Announcing "opens a menu" and then
  // presenting a plain stack of buttons misleads a screen reader, so this is the
  // caller's claim to make rather than something this button assumes.
  hasMenuPopup?: boolean;
  className?: string;
}

/**
 * The three-dot overflow trigger, borderless so it recedes until wanted.
 *
 * A plain button rather than qampo-ui-library's `IconButton`: that component bakes in
 * an opaque background, a border and hover styling, and offers only 24×24 and 38×38,
 * none of which match this control (the same reason `tertiaryIconButtonStyle` exists).
 * The library also ships no three-dot icon, so the glyph is this project's own.
 */
export const MenuDotsButton = ({
  ariaLabel,
  onClick,
  isExpanded,
  hasMenuPopup,
  className
}: MenuDotsButtonProps) => (
  <button
    type="button"
    className={`${styles.menuDotsButton} ${className ?? ''}`}
    aria-label={ariaLabel}
    aria-haspopup={hasMenuPopup === true ? 'menu' : undefined}
    aria-expanded={isExpanded}
    onClick={onClick}
  >
    <MenuDotsIcon />
  </button>
);
